import { useMemo, useState, useEffect } from 'react';
import { PmCard, PmHeadCell, useTableSortFilter, handleInlineEditKeyDown, type SortFilterColumn } from '../../../components/pm';
import {
  Box, Typography, Button, IconButton, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Stack,
  Select, MenuItem, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { useMeetings, useCreateMeeting, useUpdateMeeting, useDeleteMeeting } from '../../../api/hooks/useMeetings';
import { useProject } from '../../../api/hooks/useProjects';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildMeetingsReport } from '../reports/meetingsReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { MeetingDialog, MEETING_TYPES, type MeetingFull, type Teilnehmer } from '../components/MeetingDialog';
import { combineDateTimeToIso, splitIsoDateTime } from '../lib/dateTimeIso';
import { richTextToPlainText } from '../../../lib/richText';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';

interface Meeting extends MeetingFull {
  id: string;
}

interface CoreFields {
  titel: string;
  typ: string;
  datum: string; // YYYY-MM-DD (Inline-Schnellerfassung)
  ort: string;
}

function dateOnly(iso: string | null | undefined): string {
  return splitIsoDateTime(iso).date;
}

function coreFrom(m: Meeting): CoreFields {
  return { titel: m.titel ?? '', typ: m.typ ?? '', datum: dateOnly(m.datum), ort: m.ort ?? '' };
}

function tnSummary(m: Meeting, anwesendLabel: string, eingeladenLabel: string): string {
  if (Array.isArray(m.teilnehmer_liste) && m.teilnehmer_liste.length > 0) {
    const a = m.teilnehmer_liste.filter((tn: Teilnehmer) => tn.anwesend).length;
    const e = m.teilnehmer_liste.filter((tn: Teilnehmer) => tn.eingeladen).length;
    return `${a} ${anwesendLabel} / ${e} ${eingeladenLabel}`;
  }
  return m.teilnehmer ?? '';
}

export function MeetingsTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useMeetings(projectId);
  const { data: projectResp } = useProject(projectId);
  const createMut = useCreateMeeting(projectId);
  const updateMut = useUpdateMeeting(projectId);
  const deleteMut = useDeleteMeeting(projectId);
  const items = ((data as unknown as { data?: Meeting[] } | undefined)?.data ?? []);
  const projectName = (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const [dialogMeeting, setDialogMeeting] = useState<MeetingFull | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const anwesendLabel = t('meetings.anwesend', 'Anwesend').toLowerCase();
  const eingeladenLabel = t('meetings.eingeladen', 'Eingeladen').toLowerCase();

  const columns = useMemo<SortFilterColumn<Meeting>[]>(() => [
    { key: 'datum', label: t('meetings.datum', 'Datum'), value: (m) => dateOnly(m.datum) },
    { key: 'titel', label: t('meetings.titel', 'Name'), filter: { type: 'text' }, value: (m) => m.titel ?? '' },
    {
      key: 'typ', label: t('meetings.typ', 'Typ'),
      filter: { type: 'enum', options: MEETING_TYPES.map((tp) => ({ value: tp, label: tp })) },
      value: (m) => m.typ ?? '',
    },
    { key: 'ort', label: t('meetings.ort', 'Ort'), filter: { type: 'text' }, value: (m) => m.ort ?? '' },
    { key: 'teilnehmer', label: t('meetings.teilnehmer', 'Teilnehmer'), sortable: false, value: (m) => tnSummary(m, anwesendLabel, eingeladenLabel) },
  ], [t, anwesendLabel, eingeladenLabel]);

  const sf = useTableSortFilter(items, columns, { tableId: 'meetings' });

  const openNew = () => { setDialogMeeting(null); setDialogOpen(true); };
  const openEdit = (m: Meeting) => { setDialogMeeting(m); setDialogOpen(true); };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const head = { color: 'var(--pm-fg-subtle)', fontWeight: 600 };
  const pdfMeta: ReportMeta = { projectName, pageTitle: t('meetings.title', 'Meetings') };
  const buildReport = () => buildMeetingsReport({ items: items.map((m) => ({ ...m, datum: dateOnly(m.datum) })) });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('meetings.title', 'Meetings')}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PdfExportButton meta={pdfMeta} build={buildReport} />
          <Button variant="contained" startIcon={<EventIcon />} onClick={openNew}>
            {t('meetings.startBtn', 'Meeting starten')}
          </Button>
        </Stack>
      </Stack>

      <PmCard flush>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid var(--pm-bg-muted)' }}>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('meetings.inlineHint', 'Oben direkt eintragen und Enter — Name + Datum genügen. Für alle Felder (Teilnehmer, Ort, Start/Ende, Protokoll, Anhänge) das Stift-Symbol.')}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <PmHeadCell
                  key={col.key}
                  label={col.label}
                  align={col.align}
                  filter={col.filter}
                  sortActive={sf.sortKey === col.key}
                  sortDir={sf.sortDir}
                  onToggleSort={() => sf.toggleSort(col.key)}
                  filterValue={sf.filters[col.key]}
                  onSetFilter={(v) => sf.setFilter(col.key, v)}
                  onClearFilter={() => sf.clearFilter(col.key)}
                  headSx={head}
                  minWidth={col.key === 'titel' ? 200 : col.key === 'teilnehmer' ? 160 : 140}
                />
              ))}
              <TableCell sx={head} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AddMeetingRow
              defaultDatum={dayjs().format('YYYY-MM-DD')}
              onCreate={async (core) => {
                await createMut.mutateAsync({
                  titel: core.titel.trim() || null, typ: core.typ || null,
                  datum: combineDateTimeToIso(core.datum, '00:00'),
                  ort: core.ort.trim() || null, notes: null,
                });
              }}
            />
            {sf.rows.map((m) => (
              <MeetingRow
                key={m.id}
                item={m}
                summary={tnSummary(m, anwesendLabel, eingeladenLabel)}
                onSave={async (core) => {
                  await updateMut.mutateAsync({
                    id: m.id,
                    data: {
                      titel: core.titel.trim() || null, typ: core.typ || null,
                      datum: combineDateTimeToIso(core.datum, splitIsoDateTime(m.datum).time || '00:00'),
                      ort: core.ort.trim() || null,
                    },
                  });
                }}
                onEditDetail={() => openEdit(m)}
                onDelete={() => setDeleteId(m.id)}
              />
            ))}
            {sf.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                    {items.length === 0
                      ? t('meetings.emptyHint', 'Lege das erste Meeting an um zu starten.')
                      : t('meetings.filterEmpty', 'Keine Treffer fuer die aktuelle Filter-Kombination.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PmCard>

      <MeetingDialog open={dialogOpen} projectId={projectId} meeting={dialogMeeting} onClose={() => setDialogOpen(false)} />

      <ConfirmDialog
        open={!!deleteId}
        title={t('meetings.deleteTitle', 'Meeting löschen?')}
        message={t('meetings.deleteConfirm', 'Dieses Meeting wird endgültig entfernt.')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Stack>
  );
}

function AddMeetingRow({
  defaultDatum, onCreate,
}: {
  defaultDatum: string;
  onCreate: (core: CoreFields) => Promise<void>;
}) {
  const { t } = useTranslation('projects');
  const empty = (): CoreFields => ({ titel: '', typ: '', datum: defaultDatum, ort: '' });
  const [form, setForm] = useState<CoreFields>(empty);
  const [busy, setBusy] = useState(false);
  const canCreate = form.titel.trim().length > 0 && !!form.datum;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate(form); setForm(empty()); } finally { setBusy(false); }
  };
  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      <TableCell>
        <TextFieldDate value={form.datum} onChange={(v) => setForm({ ...form, datum: v })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextFieldStd autoFocus placeholder={t('meetings.titelPlaceholder', 'Meeting-Name eingeben…')} value={form.titel} onChange={(v) => setForm({ ...form, titel: v })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <Select variant="standard" fullWidth displayEmpty value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value })}>
          <MenuItem value="">—</MenuItem>
          {MEETING_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell>
        <TextFieldStd placeholder={t('meetings.ort', 'Ort')} value={form.ort} onChange={(v) => setForm({ ...form, ort: v })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>{t('meetings.tnViaPencil', 'Teilnehmer über Stift')}</Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('meetings.add', 'Neues Meeting')}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function MeetingRow({
  item, summary, onSave, onEditDetail, onDelete,
}: {
  item: Meeting;
  summary: string;
  onSave: (core: CoreFields) => Promise<void>;
  onEditDetail: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState<CoreFields>(coreFrom(item));
  const autosave = useAutosave<CoreFields>({
    canSave: (f) => !!f.datum,
    onSave: async (f) => { await onSave(f); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm(coreFrom(item));
  }, [autosave.isDirty, item.titel, item.typ, item.datum, item.ort]);

  const update = (patch: Partial<CoreFields>) => { const next = { ...form, ...patch }; setForm(next); autosave.schedule(next); };
  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);

  const notesPlain = richTextToPlainText(item.notes);
  const notesPreview = notesPlain ? `${notesPlain.slice(0, 80)}${notesPlain.length > 80 ? '…' : ''}` : '';

  return (
    <TableRow hover>
      <TableCell>
        <TextFieldDate value={form.datum} onChange={(v) => update({ datum: v })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextFieldStd value={form.titel} placeholder={t('meetings.titel', 'Name')} onChange={(v) => update({ titel: v })} onKeyDown={onKeyDown} />
        {notesPreview && <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>{notesPreview}</Typography>}
      </TableCell>
      <TableCell>
        <Select variant="standard" fullWidth displayEmpty value={form.typ} onChange={(e) => update({ typ: e.target.value })}>
          <MenuItem value="">—</MenuItem>
          {MEETING_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell>
        <TextFieldStd value={form.ort} placeholder={t('meetings.ort', 'Ort')} onChange={(v) => update({ ort: v })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: 'var(--pm-fg-muted)' }}>{summary || '—'}</Typography>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
          <IconButton size="small" onClick={onEditDetail} aria-label={tc('actions.edit', 'Bearbeiten')}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// kleine Inline-Helfer (vermeiden Wiederholung der TextField-Props)
function TextFieldStd({ value, onChange, onKeyDown, placeholder, autoFocus }: { value: string; onChange: (v: string) => void; onKeyDown: (e: React.KeyboardEvent) => void; placeholder?: string; autoFocus?: boolean }) {
  return <TextField variant="standard" fullWidth autoFocus={autoFocus} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />;
}
function TextFieldDate({ value, onChange, onKeyDown }: { value: string; onChange: (v: string) => void; onKeyDown: (e: React.KeyboardEvent) => void }) {
  return <TextField variant="standard" fullWidth type="date" value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />;
}
